import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { FitApiClientService, pinGetter } from './fit-api-client.service';
import * as defaultTranslations from '../../assets/translations/default.json';

export interface Translation {
    [key: string]: string | Translation;
}

@Injectable({
    providedIn: 'root'
})

export class TranslationService {
    private translations = new BehaviorSubject<Translation>({});
    private appPin: string | null = null;
    private readonly STORAGE_KEY_PREFIX = 'cms_translations';
    private translationsLoaded = new BehaviorSubject<boolean>(false);

    constructor(private fitApi: FitApiClientService) {
        this.loadLocalTranslations();
        this.loadPersistedTranslations();
        //don't load translations from backend here because we may get circular dependency error
    }

    public initializeAfterLogin(): void {
        // Get the PIN dynamically when this method is called (after login)
        this.appPin = pinGetter();
        
        try {
            this.loadLocalTranslations();
            this.loadPersistedTranslations();
            this.loadBackendTranslations().subscribe(() => {
                this.translationsLoaded.next(true);
            });
        } catch (error) {
            console.error('❌ Failed to load translations file', error);
            this.translations.next({});
            this.translationsLoaded.next(true);
        }
    }

    public getTranslation(key: string): string {
        const translations = this.translations.getValue();
        return translations[key] !== undefined ? translations[key] as string : key;
    }

    public getTranslations(): Observable<Translation> {
        return this.translations.asObservable();
    }

    private getStorageKey(): string {
        return `${this.STORAGE_KEY_PREFIX}_${this.appPin}`;
    }

    /*
    public areTranslationsLoaded(): Observable<boolean> {
        return this.translationsLoaded.asObservable();
    }
    */

    private loadLocalTranslations(): void {
        this.translations.next(defaultTranslations as Translation);
    }

    private loadPersistedTranslations(): void {
        const persistedTranslations = localStorage.getItem(this.getStorageKey());
        if (!persistedTranslations || persistedTranslations === 'undefined') {
            return;
        }

        const currentTranslations = this.translations.getValue();
        const mergedTranslations = this.deepMerge(currentTranslations, JSON.parse(persistedTranslations));
        this.translations.next(mergedTranslations);
        this.translationsLoaded.next(true); // Mark as loaded since we have persisted translations
    }

    private loadBackendTranslations(): Observable<void> {
        if (!this.appPin) {
            return of();
        }

        return from(this.fitApi.get<Translation>('/admin/cms-translate/all')).pipe(
            map(response => {
                const backendTranslations = response.data;
                //persist backend translations
                localStorage.setItem(this.getStorageKey(), JSON.stringify(backendTranslations));
                
                const currentTranslations = this.translations.getValue();
                const mergedTranslations = this.deepMerge(currentTranslations, backendTranslations);
                this.translations.next(mergedTranslations);
            }),
            catchError((error) => {
                console.error('❌ Failed to load backend translations', error);
                return of();
            })
        );
    }

    private deepMerge(target: Translation, source: Translation): Translation {
        const result = { ...target };
        
        for (const key in source) {
            result[key] = source[key] instanceof Object && key in target
                ? this.deepMerge(target[key] as Translation, source[key] as Translation)
                : source[key];
        }
        
        return result;
    }
}
